import React, { useState, useCallback, useMemo } from 'react';
import {
    Text,
    TextInput,
    View,
    Pressable,
} from 'react-native';
import { DbAmountDisplayProps } from './DbAmountDisplay.types';
import { 
    styles, 
    getStepperButtonStyle, 
    getStepperIconStyle 
} from './DbAmountDisplay.styles';

export const DbAmountDisplay: React.FC<DbAmountDisplayProps> = ({
    amount,
    currency = '￥',
    mainFontSize = 24,
    subFontSize,
    mainColor = '#333333',
    subColor,
    style,
    mainFontWeight = 'bold',
    // 原价配置
    originalAmount,
    originalFontSize,
    originalColor = '#999999',
    originalGap = 8,
    // 可编辑模式
    editable = false,
    onChange,
    min = 0,
    max = 999999.99,
    decimalPlaces = 2,
    disabled = false,
    // 步进器模式
    showStepper = false,
    step = 1,
    stepperSize = 'medium',
    stepperColor = '#333333',
    // 样式
    containerStyle,
    inputStyle,
}) => {
    const [isFocused, setIsFocused] = useState(false);
    const [inputValue, setInputValue] = useState(amount.toFixed(decimalPlaces));

    // 计算派生值
    const computedSubFontSize = subFontSize ?? mainFontSize / 2;
    const computedSubColor = subColor ?? mainColor;
    const computedOriginalFontSize = originalFontSize ?? mainFontSize * 0.6;

    // 将金额拆分为整数部分和小数部分
    const [integer, decimal] = amount.toFixed(decimalPlaces).split('.');
    const [originalInteger, originalDecimal] = (originalAmount ?? 0).toFixed(decimalPlaces).split('.');

    // 限制值在范围内
    const clampValue = useCallback((value: number): number => {
        return Math.min(Math.max(value, min), max);
    }, [min, max]);

    // 处理步进
    const handleStep = useCallback((direction: 'increase' | 'decrease') => {
        if (disabled) return;
        
        const newValue = direction === 'increase' 
            ? clampValue(amount + step)
            : clampValue(amount - step);
        
        // 确保精度
        const roundedValue = Number(newValue.toFixed(decimalPlaces));
        setInputValue(roundedValue.toFixed(decimalPlaces));
        onChange?.(roundedValue);
    }, [amount, step, clampValue, decimalPlaces, disabled, onChange]);

    // 处理输入变化
    const handleInputChange = useCallback((text: string) => {
        // 只允许数字和小数点
        const cleanText = text.replace(/[^0-9.]/g, '');
        
        // 防止多个小数点
        const parts = cleanText.split('.');
        let formattedText = parts[0];
        if (parts.length > 1) {
            formattedText += '.' + parts[1].slice(0, decimalPlaces);
        }
        
        setInputValue(formattedText);
    }, [decimalPlaces]);

    // 处理输入完成
    const handleInputEnd = useCallback(() => {
        setIsFocused(false);
        
        let numericValue = parseFloat(inputValue) || 0;
        numericValue = clampValue(numericValue);
        
        const roundedValue = Number(numericValue.toFixed(decimalPlaces));
        setInputValue(roundedValue.toFixed(decimalPlaces));
        onChange?.(roundedValue);
    }, [inputValue, clampValue, decimalPlaces, onChange]);

    // 判断步进按钮是否禁用
    const isDecreaseDisabled = disabled || amount <= min;
    const isIncreaseDisabled = disabled || amount >= max;

    // 步进按钮样式
    const stepperButtonStyle = useMemo(() => [
        styles.stepperButton,
        getStepperButtonStyle(stepperSize),
    ], [stepperSize]);

    const stepperIconStyle = useMemo(() => [
        styles.stepperIcon,
        getStepperIconStyle(stepperSize),
        { color: stepperColor },
    ], [stepperSize, stepperColor]);

    // 渲染步进按钮
    const renderStepperButton = (
        type: 'decrease' | 'increase',
        isDisabled: boolean
    ) => (
        <Pressable
            onPress={() => handleStep(type)}
            disabled={isDisabled}
            style={({ pressed }) => [
                ...stepperButtonStyle,
                pressed && !isDisabled && styles.stepperButtonPressed,
                isDisabled && styles.stepperButtonDisabled,
            ]}
        >
            <Text style={stepperIconStyle}>
                {type === 'decrease' ? '−' : '+'}
            </Text>
        </Pressable>
    );

    // 纯展示模式
    if (!editable && !showStepper) {
        return (
            <View style={[styles.displayContainer, style]}>
                <Text>
                    {/* 币种符号 */}
                    <Text style={{ fontSize: computedSubFontSize, color: computedSubColor }}>
                        {currency}
                    </Text>
                    {/* 整数部分 */}
                    <Text style={{
                        fontSize: mainFontSize,
                        color: mainColor,
                        fontWeight: mainFontWeight
                    }}>
                        {integer}
                    </Text>
                    {/* 小数部分 */}
                    <Text style={{ fontSize: computedSubFontSize, color: computedSubColor }}>
                        .{decimal}
                    </Text>
                </Text>
                
                {/* 原价（删除线） */}
                {originalAmount !== undefined && originalAmount > 0 && (
                    <View style={{ marginLeft: originalGap, flexDirection: 'row', alignItems: 'baseline' }}>
                        {/* 原价币种符号 */}
                        <Text style={{
                            fontSize: computedOriginalFontSize * 0.6,
                            color: originalColor,
                            textDecorationLine: 'line-through',
                            textDecorationStyle: 'solid',
                        }}>
                            {currency}
                        </Text>
                        {/* 原价整数部分 */}
                        <Text style={{
                            fontSize: computedOriginalFontSize,
                            color: originalColor,
                            textDecorationLine: 'line-through',
                            textDecorationStyle: 'solid',
                        }}>
                            {originalInteger}
                        </Text>
                        {/* 原价小数部分 */}
                        <Text style={{
                            fontSize: computedOriginalFontSize * 0.6,
                            color: originalColor,
                            textDecorationLine: 'line-through',
                            textDecorationStyle: 'solid',
                        }}>
                            .{originalDecimal}
                        </Text>
                    </View>
                )}
            </View>
        );
    }

    // 可编辑模式或步进器模式
    return (
        <View style={[styles.wrapper, containerStyle]}>
            {/* 减少按钮 */}
            {showStepper && renderStepperButton('decrease', isDecreaseDisabled)}
            
            {showStepper && <View style={styles.separator} />}
            
            {/* 金额显示/输入区域 */}
            <View style={[
                editable && styles.inputContainer,
                editable && isFocused && styles.inputContainerFocused,
                editable && disabled && styles.inputContainerDisabled,
                inputStyle,
            ]}>
                {/* 币种符号 */}
                <Text style={[
                    styles.currencySymbol,
                    { fontSize: computedSubFontSize, color: computedSubColor }
                ]}>
                    {currency}
                </Text>
                
                {editable ? (
                    <TextInput
                        style={[
                            styles.input,
                            { fontSize: mainFontSize, color: mainColor, fontWeight: mainFontWeight },
                            disabled && styles.inputDisabled,
                        ]}
                        value={inputValue}
                        onChangeText={handleInputChange}
                        onFocus={() => setIsFocused(true)}
                        onBlur={handleInputEnd}
                        onEndEditing={handleInputEnd}
                        keyboardType="decimal-pad"
                        editable={!disabled}
                        selectTextOnFocus
                        placeholder="0.00"
                        placeholderTextColor="#CCCCCC"
                    />
                ) : (
                    <View style={styles.amountWrapper}>
                        <Text style={{ 
                            fontSize: mainFontSize, 
                            color: mainColor, 
                            fontWeight: mainFontWeight 
                        }}>
                            {integer}
                        </Text>
                        <Text style={{ fontSize: computedSubFontSize, color: computedSubColor }}>
                            .{decimal}
                        </Text>
                    </View>
                )}
            </View>
            
            {showStepper && <View style={styles.separator} />}
            
            {/* 增加按钮 */}
            {showStepper && renderStepperButton('increase', isIncreaseDisabled)}
        </View>
    );
};

// 保持向后兼容的别名
export const AmountDisplay = DbAmountDisplay;

export default DbAmountDisplay;
